import { computed } from 'vue'
import { useRouter, RouteRecordNormalized } from 'vue-router'
import { usePermissions } from './usePermissions'
import { useAuthStore } from '../stores/auth'
import { getRoutePermissions, type PermissionRequirement } from '../config/permissions'

export interface AccessCheckOptions {
  routeName?: string
  route?: RouteRecordNormalized
  projectId?: number
}

export function useAccessControl() {
  const router = useRouter()
  const { hasAnyPermission, hasAllPermissions } = usePermissions()
  const authStore = useAuthStore()

  const isAuthenticated = computed(() => authStore.isAuthenticated)

  const getRouteRecordByName = (name: string | symbol): RouteRecordNormalized | undefined => {
    const match = router.getRoutes().find((r) => r.name === name)
    return match
  }

  const extractPermissions = (
    route: RouteRecordNormalized | undefined,
  ): {
    requiredAll: string[]
    requiredAny: string[]
    requiresAuth: boolean
  } => {
    if (!route) return { requiredAll: [], requiredAny: [], requiresAuth: false }
    const requiresAuth = Boolean(route.meta?.requiresAuth)

    const requiredAll = (route.meta?.requiredPermissions as string[] | undefined) || []
    const requiredAny = (route.meta?.requiredAnyPermissions as string[] | undefined) || []

    return { requiredAll, requiredAny, requiresAuth }
  }

  const canAccessRoute = async (options: AccessCheckOptions): Promise<boolean> => {
    const route = options.route ?? (options.routeName ? getRouteRecordByName(options.routeName) : undefined)
    // console.log(`[useAccessControl] canAccessRoute called for:`, options.routeName || route?.name)

    const { requiredAll, requiredAny, requiresAuth } = extractPermissions(route)
    // console.log(`[useAccessControl] Extracted permissions:`, { requiredAll, requiredAny, requiresAuth })

    // Determine projectId based on the TARGET route shape. For org-scoped routes, force undefined.
    let projectId = options.projectId
    try {
      const currentRoute = router.currentRoute.value
      const targetPath = route?.path || ''
      const routeName = String(route?.name || options.routeName || '')

      // Heuristics: project-scoped if explicit :projectId or legacy ':id' under project routes
      const isProjectScoped =
        targetPath.includes(':projectId') || (targetPath.includes(':id') && routeName.startsWith('project-'))

      if (!isProjectScoped) {
        // Force org-scoped checks to ignore provided projectId
        projectId = undefined
      } else if (!projectId) {
        // Derive from current route params when needed
        projectId = currentRoute.params.projectId
          ? parseInt(currentRoute.params.projectId as string)
          : currentRoute.params.id
            ? parseInt(currentRoute.params.id as string)
            : undefined
      }
    } catch (_) {
      // console.log(`[useAccessControl] Could not derive projectId; defaulting to`, projectId)
      // keep whatever was passed in
    }
    // console.log(`[useAccessControl] Project ID from route:`, projectId)

    // Organization owners have full access to all organization pages, except superuser-only pages
    if (authStore.user?.is_owner && !route?.meta?.requiresSuperuser) {
      // console.log(`[useAccessControl] User is owner, granting access`)
      return true
    }

    // Prefer centralized permission configuration when available
    try {
      const routeName = String(route?.name || options.routeName || '')
      if (routeName) {
        const centralized: PermissionRequirement | null = getRoutePermissions(routeName)
        if (centralized) {
          // console.log(`[useAccessControl] Using centralized permissions for ${routeName}:`, centralized)
          const { hasAnyPermission, hasAllPermissions } = usePermissions()
          const roles = centralized.roles.map((r) => r.toString())
          const ok =
            centralized.type === 'ANY'
              ? await hasAnyPermission(roles, options.projectId)
              : await hasAllPermissions(roles, options.projectId)
          // console.log(`[useAccessControl] Centralized result for ${routeName}:`, ok)
          if (!ok) return false
          // If centralized check passes, allow regardless of legacy meta
          return true
        }
      }
    } catch (e) {
      console.warn(`[useAccessControl] Centralized permission check failed with error, falling back to legacy`, e)
    }

    // Deny by default for authenticated areas when no explicit permissions provided (legacy only)
    if (requiresAuth && requiredAll.length === 0 && requiredAny.length === 0) {
      // console.log(`[useAccessControl] Requires auth but no explicit permissions (legacy), denying`)
      return false
    }

    // If there are explicit requirements, enforce them
    if (requiredAll.length > 0) {
      const okAll = await hasAllPermissions(requiredAll, projectId)
      // console.log(`[useAccessControl] Required all result:`, okAll, `for permissions:`, requiredAll)
      if (!okAll) {
        // console.log(`[useAccessControl] Access denied - missing required permissions:`, requiredAll)
        return false
      }
    }

    if (requiredAny.length > 0) {
      const okAny = await hasAnyPermission(requiredAny, projectId)
      // console.log(`[useAccessControl] Required any result:`, okAny, `for permissions:`, requiredAny)
      if (!okAny) {
        // console.log(`[useAccessControl] Access denied - missing any of required permissions:`, requiredAny)
        return false
      }
    }

    // If no requirements and not authenticated area, allow
    if (!requiresAuth) {
      // console.log(`[useAccessControl] No auth required, allowing`)
      return true
    }

    // If we have explicit requirements and they were met, allow access
    if (requiredAll.length > 0 || requiredAny.length > 0) {
      // console.log(`[useAccessControl] Explicit requirements met, allowing access`)
      return true
    }

    // Otherwise, deny by default for authenticated areas without explicit permissions
    // console.log(`[useAccessControl] Denying by default - no explicit permissions`)
    return false
  }

  const filterRoutesForMenu = async (routes: Array<{ name: string; children?: any[] }>, projectId?: number) => {
    const results: any[] = []
    for (const r of routes) {
      const can = await canAccessRoute({ routeName: r.name, projectId })
      if (!can) continue
      if (Array.isArray(r.children) && r.children.length > 0) {
        const filteredChildren = await filterRoutesForMenu(r.children, projectId)
        if (filteredChildren.length > 0) {
          results.push({ ...r, children: filteredChildren })
        } else {
          // If the route is a section with no accessible children, skip it
          continue
        }
      } else {
        results.push(r)
      }
    }
    return results
  }

  return {
    canAccessRoute,
    filterRoutesForMenu,
    isAuthenticated,
  }
}
